import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { getAllDistricts, updateSchool } from '../../api/content-api';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import District from '../../model/District';
import School from '../../model/School';
import { useNavigate } from 'react-router-dom';
import { loginPage } from '../../store/fixed-routing';
import { RSPO_VOIVODESHIPS } from '../../constants/rspo-voivodeships';

type Props = {
	open: boolean;
	school: School | null;
	onClose: () => void;
	onSaved: () => void;
};

const EditSchoolDialog = ({ open, school, onClose, onSaved }: Props) => {
	const { token, logout } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();
	const [saving, setSaving] = useState(false);
	const [districts, setDistricts] = useState<District[]>([]);
	const [form, setForm] = useState({
		rspo: 0,
		name: '',
		districtId: 0,
		street: '',
		building: '',
		apartment: '',
		zipCode: '',
		city: '',
		wojewodztwo: '',
		powiat: '',
	});

	useEffect(() => {
		if (school && open) {
			setForm({
				rspo: school.rspo,
				name: school.name,
				districtId: school.district.id,
				street: school.street,
				building: school.building,
				apartment: school.apartment,
				zipCode: school.zipCode,
				city: school.city,
				wojewodztwo: school.wojewodztwo || '',
				powiat: school.powiat || '',
			});
		}
	}, [school, open]);

	useEffect(() => {
		if (!open) {
			return;
		}
		getAllDistricts(token)
			.then((response) => {
				if (response.status === 200) {
					setDistricts(response.data.map((d: any) => District.fromApiResponse(d)));
				}
			})
			.catch(() => setMsg({ msg: 'Nie udało się wczytać okręgów', severity: 'error' }));
	}, [open, token, setMsg]);

	const handleSave = async () => {
		if (!token || !school) {
			return;
		}
		setSaving(true);
		try {
			const res = await updateSchool(token, school.id, {
				rspo: form.rspo,
				name: form.name,
				districtId: form.districtId,
				street: form.street,
				building: form.building,
				apartment: form.apartment,
				zipCode: form.zipCode,
				city: form.city,
				wojewodztwo: form.wojewodztwo || '',
				powiat: form.powiat || '',
			});
			if (res.status === 200) {
				setMsg({ msg: 'Zapisano szkołę', severity: 'success' });
				onSaved();
				onClose();
			} else {
				setMsg({ msg: (res.data as { message?: string })?.message ?? 'Błąd', severity: 'error' });
			}
		} catch (e: any) {
			if (e?.response?.status === 401 || e?.response?.status === 403) {
				logout();
				navigate(loginPage);
				setMsg({ msg: 'Sesja wygasła', severity: 'warning' });
			} else {
				setMsg({
					msg: e?.response?.data?.message ?? 'Błąd zapisu szkoły',
					severity: 'error',
				});
			}
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open={open && school !== null}
			onClose={() => !saving && onClose()}
			fullWidth
			maxWidth='sm'
		>
			<DialogTitle>Edycja szkoły</DialogTitle>
			<DialogContent>
				<Grid
					container
					spacing={2}
					sx={{ mt: 0.5 }}
				>
					<Grid
						item
						xs={12}
					>
						<TextField
							label='RSPO'
							type='number'
							fullWidth
							value={form.rspo || ''}
							onChange={(e) => setForm((f) => ({ ...f, rspo: parseInt(e.target.value, 10) || 0 }))}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<TextField
							label='Nazwa szkoły'
							fullWidth
							value={form.name}
							onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<FormControl fullWidth>
							<InputLabel id='edit-school-district'>Okręg</InputLabel>
							<Select
								labelId='edit-school-district'
								label='Okręg'
								value={form.districtId}
								onChange={(e) => setForm((f) => ({ ...f, districtId: Number(e.target.value) }))}
							>
								{districts.map((d) => (
									<MenuItem
										key={d.id}
										value={d.id}
									>
										{d.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<TextField
							label='Ulica'
							fullWidth
							value={form.street}
							onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
						/>
					</Grid>
					<Grid
						item
						xs={6}
					>
						<TextField
							label='Numer budynku'
							fullWidth
							value={form.building}
							onChange={(e) => setForm((f) => ({ ...f, building: e.target.value }))}
						/>
					</Grid>
					<Grid
						item
						xs={6}
					>
						<TextField
							label='Numer lokalu'
							fullWidth
							value={form.apartment}
							onChange={(e) => setForm((f) => ({ ...f, apartment: e.target.value }))}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<TextField
							label='Kod pocztowy'
							fullWidth
							value={form.zipCode}
							onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<TextField
							label='Miasto'
							fullWidth
							value={form.city}
							onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<FormControl fullWidth>
							<InputLabel id='edit-school-voiv'>Województwo</InputLabel>
							<Select
								labelId='edit-school-voiv'
								label='Województwo'
								value={form.wojewodztwo}
								displayEmpty
								onChange={(e) => setForm((f) => ({ ...f, wojewodztwo: String(e.target.value) }))}
							>
								<MenuItem value=''>
									<em>nieustalone</em>
								</MenuItem>
								{RSPO_VOIVODESHIPS.map((v) => (
									<MenuItem
										key={v.code}
										value={v.code}
									>
										{v.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<TextField
							label='Powiat'
							fullWidth
							value={form.powiat}
							onChange={(e) => setForm((f) => ({ ...f, powiat: e.target.value }))}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onClose}
					disabled={saving}
				>
					Anuluj
				</Button>
				<Button
					variant='contained'
					onClick={() => void handleSave()}
					disabled={saving}
				>
					{saving ? <CircularProgress size={22} /> : 'Zapisz'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EditSchoolDialog;
